import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import SSOMappingsService from '../../services/sso_mappings_service';
import i18next from 'i18next';
import { OpSenseToastContainer, toast } from '../Toast';
import { ScrollModule } from '../ScrollModule';
import ScrollArea from 'react-scrollbar';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/fontawesome-free-solid';

const SSOMapping = ({roleTypes, enterpriseId}) => {
    const [mappings, setMappings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showTestModal, setShowTestModal] = useState(false);
    const [showExpressionHelpModal, setShowExpressionHelpModal] = useState(false);
    const [testMapping, setTestMapping] = useState(null);

    useEffect(() => {
        setMappings(null);

        SSOMappingsService.getAll().then((fetchedMappings) => {
            setMappings(fetchedMappings.mappings == null ? [] : fetchedMappings.mappings.map((mapping, rowIndex) => ({
                id: mapping.id,
                roleType: mapping.roleType,
                name: mapping.displayName,
                expression: mapping.mappingExpression,
                rowIndex: rowIndex
            })));
        }).catch(error => {
            console.error(error);
            toast.error('Error getting existing mappings.');
        });
    }, [roleTypes, enterpriseId]);

    const addRow = () => {
        const rowIndexes = mappings.map(mapping => mapping.rowIndex);
        const nextRowIndex = mappings.length == 0 ? 1 : Math.max(...rowIndexes) + 1;
        setMappings(mappings => [...mappings, {id: null, name: '', expression: '', roleType: roleTypes[0], rowIndex: nextRowIndex}]);
    }

    const removeRow = (mapping) => {
        setMappings(mappings.filter(entry => entry.rowIndex !== mapping.rowIndex));
    }

    const rowChanged = (mapping) => {
        var updatedMappings = [...mappings];
        updatedMappings.splice(mappings.findIndex(entry => entry.rowIndex === mapping.rowIndex), 1, mapping);
        setMappings(updatedMappings);
    }

    const toggleTestModal = (mapping) => {
        setTestMapping(mapping);
        setShowTestModal(!showTestModal);
    }

    const toggleExpressionHelpModal = () => {
        setShowExpressionHelpModal(!showExpressionHelpModal);
    }

    const handleSubmit = (event) => {
        event.preventDefault();

        setSaving(true);
        const mappingsDTOs = mappings.map((mapping) => ({
            id: null,
            enterprise: {id: enterpriseId},
            roleType: mapping.roleType,
            displayName: mapping.name,
            mappingExpression: mapping.expression
        }));
        SSOMappingsService.save(mappingsDTOs).then((savedMappings) => {
            setMappings(savedMappings.mappings.map((mapping, rowIndex) => ({
                id: mapping.id,
                roleType: mapping.roleType,
                name: mapping.displayName,
                expression: mapping.mappingExpression,
                rowIndex: rowIndex
            })));

            setSaving(false);
            toast.success('Mappings saved.');
        }).catch(error => {
            console.error(error);
            setSaving(false);
            toast.error('Error saving mappings.');
        });
    }

    return (
        <div>
            <OpSenseToastContainer />

            {
                mappings == null || saving ? 
                <div className="loader">{ i18next.t('loading') }</div> :
                <form onSubmit={handleSubmit}>
                    <table className='table'>
                        <thead>
                            <tr>
                                <th style={{width: "25%"}}>{ i18next.t('form-labels.name') }</th>
                                <th>{ i18next.t('sso-mapping.mapping-expression') }</th>
                                <th style={{width: "18%"}}>{ i18next.t('sso-mapping.role-type') }</th>
                                <th style={{width: "125px"}}></th>
                            </tr>
                        </thead>
                        <tbody>
                            { mappings.length == 0 ? <tr><td colSpan={4} style={{verticalAlign: 'middle'}}><div className="text-center"><strong>{ i18next.t('sso-mapping.no-expressions') }</strong></div></td></tr> :
                            mappings.map((mapping, i) => 
                                <SSOMappingTableRow 
                                    key={mapping.rowIndex} 
                                    mapping={mapping} 
                                    roleTypes={roleTypes}
                                    oddRow={!(i % 2)}
                                    removeMapping={removeRow}
                                    onChange={rowChanged}
                                    toggleTestModal={toggleTestModal}
                                    toggleExpressionHelpModal={toggleExpressionHelpModal}
                                />
                            )}

                            <tr>
                                <td colSpan={5} style={{verticalAlign: 'middle'}}>
                                    <div className="text-center">
                                        <button className="btn btn-default btn-sm" type="button" onClick={addRow}>{i18next.t('sso-mapping.add-button')}</button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <ul className="list-inline clearfix">
                        <li><button type="submit" className="btn btn-primary btn-block">{i18next.t('form-labels.save')}</button></li>
                    </ul>
                </form>
            }

            {
                testMapping ? <TestModal show={showTestModal} toggleModal={() => {toggleTestModal(null)}} testMapping={testMapping} /> : null
            }

            <SSOMappingExpressionHelpModal show={showExpressionHelpModal} toggleModal={toggleExpressionHelpModal} />
        </div>
    );
}

const TestModal = ({show, toggleModal, testMapping}) => {
    const [testResult, setTestResult] = useState(null);
    const [runningTest, setRunningTest] = useState(false);
    const [testAgainst, setTestAgainst] = useState('');
    const [testError, setTestError] = useState(null);

    const runTest = (nameToTest) => {
        setTestError(null);
        setRunningTest(true);
        SSOMappingsService.runTest(nameToTest, testMapping.roleType.id, testMapping.expression).then((result) => {
            setTestResult(result);
            setRunningTest(false);
        }).catch(error => {
            setRunningTest(false);
            if (error.response && error.response.data && error.response.data.genericSystemErrorMessage) {
                setTestError(error.response.data.genericSystemErrorMessage);
            } else {
                setTestError(i18next.t('sso-mapping.unknown-error-running-test'));
            }
        });
    }

    return (
        <Modal show={show} onHide={toggleModal} dialogClassName="op-floor-plan-modal">
            <Modal.Header closeButton>
                <Modal.Title>{i18next.t('sso-mapping.test-sso-mapping')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            {
                testMapping ?
                (
                    <div>
                        {
                            testError ? 
                            <div className='error' style={{padding: 10}}>
                                <strong style={{color: "#e00"}}>{i18next.t('sso-mapping.error-running-test')}</strong> <br/> {testError}
                            </div> : null
                        }
                        <div style={{paddingBottom: 20}}>
                        { i18next.t('form-labels.name') }: <strong>{testMapping.name}</strong> <br/>
                        { i18next.t('sso-mapping.mapping-expression') }: <strong>{testMapping.expression}</strong> <br/>
                        { i18next.t('sso-mapping.role-type') }: <strong>{testMapping.roleType.name}</strong> <br/>
                        </div>
                        <div>
                        {i18next.t('sso-mapping.example')} {runningTest}: <br/>
                            <input type="text" 
                                   className="form-control" 
                                   style={{width: "50%", paddingBottom: 10}} 
                                   value={testAgainst} 
                                   onChange={e => setTestAgainst(e.target.value)} 
                                /> <br/>
                            <button className="btn btn-default btn-sm" onClick={() => {runTest(testAgainst)}} disabled={testAgainst.length == 0}>{i18next.t('sso-mapping.run-test')}</button>
                        </div>
                        { 
                            runningTest ? <div className="loader">{ i18next.t('loading') }</div> :
                            (
                                <div style={{paddingTop: 10}}>
                                {
                                    testResult ? (
                                        testResult.roles && testResult.roles.length > 0 ? (
                                            <div>
                                                <strong>{i18next.t('sso-mapping.matching-roles')}</strong>: <br/>

                                                <ScrollModule />
                                                <ScrollArea
                                                    speed={ 0.8 }
                                                    className="scroll-container"
                                                    contentClassName="scrollable-content"
                                                    horizontal={ true }>
                                                    <ul>
                                                        {testResult.roles.map(role => (<li key={role.id}>{role.name}</li>))}
                                                    </ul>
                                                </ScrollArea>
                                            </div>
                                        ) : <div><strong>{i18next.t('sso-mapping.no-matching-roles')}</strong></div>
                                    ) : null
                                    
                                }
                                </div> 
                            )
                        }
                    </div>
                ) : null
            }
            </Modal.Body>
        </Modal>
    );
}

const SSOMappingTableRow = ({mapping, roleTypes, oddRow, removeMapping, onChange, toggleTestModal, toggleExpressionHelpModal}) => {
    const [name, setName] = useState(mapping.name);
    const [expression, setExpression] = useState(mapping.expression);
    const [roleType, setRoleType] = useState(mapping.roleType);

    const updateName = (name) => {
        setName(name);
        onChange({...mapping, name: name});
    }

    const updateExpression = (expression) => {
        setExpression(expression);
        onChange({...mapping, expression: expression});
    }

    const updateRoleType = (roleType) => {
        setRoleType(roleType);
        onChange({...mapping, roleType: roleType});
    }

    return (
        <tr className='sso-mapping' style={oddRow ? {background: "rgba(0,0,0,0.03)"} : {}}>
            <td>
                <input type='text' className='form-control' value={name} onChange={e => updateName(e.target.value)} />
            </td>
            <td>
              <div style={{display: "flex"}}>
                <textarea rows="3" className='form-control' value={expression} onChange={e => updateExpression(e.target.value)} style={{resize: 'none'}} />
                <div className='icon-container'> 
                  <FontAwesomeIcon onClick={toggleExpressionHelpModal} icon={faQuestionCircle} size={'lg'}/>
                </div>   
              </div> 
            </td>
            <td>
                <select className='form-control' value={roleType.id} onChange={e => updateRoleType(roleTypes.filter(entry => entry.id == parseInt(e.target.value))[0])}>
                    {roleTypes.map(roleTypeOption => <option key={roleTypeOption.id} value={roleTypeOption.id}>{roleTypeOption.name}</option>)}
                </select>
            </td>
            <td>
                <div className="button-stack">
                    <button className="btn btn-default btn-sm" type="button" onClick={() => {removeMapping(mapping)}}>{i18next.t('remove')}</button>
                    <button className="btn btn-default btn-sm" type="button" onClick={() => {toggleTestModal(mapping)}} disabled={expression.length == 0}>{i18next.t('test')}</button>
                </div>
            </td>
        </tr>
    );
}

// <li>opSenseRoleToken - The OpSene role token to test against.</li>
const SSOMappingExpressionHelpModal = ({show, toggleModal}) => {
    return (
        <Modal show={show} onHide={toggleModal} dialogClassName="op-floor-plan-modal">
            <Modal.Header closeButton>
                <Modal.Title>{i18next.t('sso-mapping.sso-mapping-expression')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            {i18next.t('sso-mapping.expression-instruction')} <br/>
                
                <div style={{paddingTop:10}}>
                    <strong>{i18next.t('sso-mapping.values-label')}</strong> <br/>
                    <ul>
                        <li><strong>externalName</strong> - {i18next.t('sso-mapping.externalname-legend')}</li>
                        <li><strong>opSenseRoleName</strong> - {i18next.t('sso-mapping.opsenserolename-legend')}</li>
                    </ul>
                </div>

                <p style={{paddingTop:10}}>
                    <strong>{i18next.t('sso-mapping.example-expressions')}</strong> <br/>
                </p>

                <pre>
                    externalName == opSenseRoleName
                </pre>

                <pre style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                    externalName.split(" ").length == 2 && opSenseRoleName.split(" ").length == 4 && opSenseRoleName.split(" ")[0] == externalName.split(" ")[1]
                </pre>
            </Modal.Body>
        </Modal>
    );
}

export default SSOMapping;